import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import finalLogo from '../../assets/logo/final-logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const scrolled = true;
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const [activeMobileSubDropdown, setActiveMobileSubDropdown] = useState(null);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const getNavLinkTranslationKey = (name) => {
    switch (name) {
      case 'Home': return 'navbar.home';
      case 'About': return 'navbar.about';
      case 'Services': return 'navbar.services';
      case 'Gold Loan': return 'navbar.goldLoan';
      case 'Mortgage Loan': return 'navbar.mortgageLoan';
      case 'Loan Against Deposit': return 'navbar.loanAgainstDeposit';
      case 'Business Loan': return 'navbar.businessLoan';
      case 'Agriculture Allied Loan': return 'navbar.agricultureLoan';
      case 'Vehicle Loan': return 'navbar.vehicleLoan';
      case 'Deposit Schemes': return 'navbar.depositSchemes';
      case 'Members': return 'navbar.members';
      case 'News': return 'navbar.news';
      case 'Branches': return 'navbar.branches';
      case 'Gallery': return 'navbar.gallery';
      case 'Careers': return 'navbar.careers';
      case 'Contact': return 'navbar.contact';
      case 'Apply Loan': return 'navbar.applyLoan';
      case 'Apply Loan Now': return 'navbar.applyLoanNow';
      default: return name;
    }
  };

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'About', path: '/about' },
    {
      name: 'Services',
      path: '#',
      dropdown: [
        { name: 'Gold Loan', path: '/services/gold-loan' },
        { 
          name: 'Mortgage Loan', 
          path: '/services/mortgage-loan',
          subItems: [
            { name: 'Loan Against Deposit', path: '/services/loan-against-deposit' }
          ]
        },
        { name: 'Business Loan', path: '/services/business-loan' },
        { name: 'Agriculture Allied Loan', path: '/services/agriculture-allied-loan' },
        { name: 'Vehicle Loan', path: '/services/vehicle-loan' },
      ],
    },
    { name: 'Deposit Schemes', path: '/deposit-schemes' },
    { name: 'Members', path: '/members' },
    { name: 'News', path: '/news' },
    { name: 'Branches', path: '/branches' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="fixed top-0 w-full z-[999] transition-all duration-500">
      {/* Isolated Glassmorphic & Shadow Background Overlay */}
      <div 
        className={cn(
          'absolute inset-0 z-0 transition-all duration-500 pointer-events-none',
          scrolled 
            ? 'bg-white shadow-xl' 
            : 'bg-black/25 backdrop-blur-md border-b border-white/10'
        )}
      />

      {/* Main Navbar Container */}
      <div 
        className={cn(
          'relative z-10 w-full max-w-none px-6 lg:px-16 transition-all duration-500',
          scrolled ? 'py-3.5' : 'py-6'
        )}
      >
        <div className="flex justify-between items-center">
          {/* Logo Section */}
          <Link to="/" className="flex items-center space-x-3 group shrink-0">
            <img 
              src={finalLogo} 
              alt="Sangram Patsanstha Logo" 
              className="w-10 h-10 object-contain rounded-full bg-white p-0.5 group-hover:rotate-12 transition-transform duration-300 shadow-lg"
            />
            <div className="flex flex-col">
              <span className={cn(
                "text-xl font-bold tracking-tighter block leading-none",
                scrolled ? "text-primary" : "text-white"
              )}>SANGRAM</span>
              <span className={cn(
                "text-[9px] uppercase tracking-[0.3em] font-bold block mt-1",
                scrolled ? "text-primary-dark/80" : "text-white/80"
              )}>Patsanstha</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-[18px] xl:space-x-[24px] ml-auto pl-8">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative group"
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                onMouseLeave={() => link.dropdown && setActiveDropdown(null)}
              >
                {link.dropdown ? (
                  <button className={cn(
                    "flex items-center space-x-1.5 font-semibold text-[13.5px] xl:text-[14.5px] uppercase tracking-wider transition-colors duration-300 whitespace-nowrap",
                    scrolled ? "text-primary-dark hover:text-primary" : "text-white hover:text-secondary"
                  )}>
                    <span>{t(getNavLinkTranslationKey(link.name))}</span>
                    <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                  </button>
                ) : (
                  <Link
                    to={link.path}
                    className={cn(
                      "font-semibold text-[13.5px] xl:text-[14.5px] uppercase tracking-wider transition-colors duration-300 whitespace-nowrap",
                      scrolled ? "text-primary-dark hover:text-primary" : "text-white hover:text-secondary"
                    )}
                  >
                    {t(getNavLinkTranslationKey(link.name))}
                  </Link>
                )}

                {/* Dropdown Menu */}
                {link.dropdown && (
                  <AnimatePresence>
                    {activeDropdown === link.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-2xl py-2 border border-gray-100"
                      >
                        {link.dropdown.map((sub) => {
                          if (sub.subItems) {
                            return (
                              <div
                                key={sub.name}
                                className="relative"
                                onMouseEnter={() => setActiveSubDropdown(sub.name)}
                                onMouseLeave={() => setActiveSubDropdown(null)}
                              >
                                <div className="flex justify-between items-center px-4 py-2 text-sm text-gray-700 hover:bg-primary hover:text-white transition-colors duration-200 cursor-pointer">
                                  <Link to={sub.path} className="flex-grow">
                                    {t(getNavLinkTranslationKey(sub.name))}
                                  </Link>
                                  <ChevronRight className="w-4 h-4 ml-2" />
                                </div>
                                <AnimatePresence>
                                  {activeSubDropdown === sub.name && (
                                    <motion.div
                                      initial={{ opacity: 0, x: 10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      exit={{ opacity: 0, x: 10 }}
                                      className="absolute left-full top-0 -mt-2 w-56 bg-white rounded-xl shadow-2xl py-2 border border-gray-100 z-50"
                                    >
                                      {sub.subItems.map((child) => (
                                        <Link
                                          key={child.name}
                                          to={child.path}
                                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-primary hover:text-white transition-colors duration-200 whitespace-nowrap"
                                        >
                                          {t(getNavLinkTranslationKey(child.name))}
                                        </Link>
                                      ))}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          }
                          return (
                            <Link
                              key={sub.name}
                              to={sub.path}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-primary hover:text-white transition-colors duration-200 whitespace-nowrap"
                            >
                              {t(getNavLinkTranslationKey(sub.name))}
                            </Link>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}

            <Link
              to="/apply-loan"
              className="bg-primary hover:bg-primary/90 text-white px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-md shadow-primary/20 whitespace-nowrap ml-4 live-button-glow"
            >
              {t('navbar.applyLoan')}
            </Link>

            {/* Language Switcher for Desktop */}
            <div className={cn(
              "flex items-center space-x-0.5 p-1 rounded-full border transition-all duration-300 ml-4 shadow-sm",
              scrolled 
                ? "bg-gray-100 border-gray-200" 
                : "bg-white/15 border-white/20 backdrop-blur-md"
            )}>
              <button
                onClick={() => i18n.changeLanguage('mr')}
                className={cn(
                  "px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider transition-all duration-300",
                  i18n.language === 'mr'
                    ? (scrolled ? "bg-primary-dark text-white shadow-sm" : "bg-white text-primary-dark shadow-md")
                    : (scrolled ? "text-primary-dark/70 hover:text-primary-dark hover:bg-gray-200/50" : "text-white/70 hover:text-white hover:bg-white/10")
                )}
              >
                मराठी
              </button>
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-300",
                  i18n.language === 'en'
                    ? (scrolled ? "bg-primary-dark text-white shadow-sm" : "bg-white text-primary-dark shadow-md")
                    : (scrolled ? "text-primary-dark/70 hover:text-primary-dark hover:bg-gray-200/50" : "text-white/70 hover:text-white hover:bg-white/10")
                )}
              >
                EN
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "p-2 rounded-md transition-colors",
                scrolled ? "text-primary" : "text-white"
              )}
            >
              {isOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer (Isolated from background blur contexts) */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[9998] lg:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Sliding Drawer Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-[85%] max-w-sm bg-white z-[9999] shadow-2xl lg:hidden flex flex-col overflow-hidden"
            >
              {/* Premium dark header box */}
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-primary-dark text-white flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <img 
                    src={finalLogo} 
                    alt="Sangram Logo" 
                    className="w-8 h-8 object-contain rounded-full bg-white p-0.5 shadow-md"
                  />
                  <div className="flex flex-col">
                    <span className="text-lg font-bold tracking-tighter text-white leading-none">SANGRAM</span>
                    <span className="text-[8px] uppercase tracking-[0.2em] font-semibold text-white/70 mt-0.5">Patsanstha</span>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="p-2 hover:bg-white/10 rounded-xl transition-all"
                >
                  <X className="w-7 h-7 text-white" />
                </button>
              </div>

              {/* Scrollable menu options */}
              <div className="flex-grow overflow-y-auto py-6 px-6 sidebar-scroll">
                {/* Language Switcher for Mobile */}
                <div className="mb-6 pb-6 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {t('navbar.languageTitle')}
                  </span>
                  <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-full border border-gray-200">
                    <button
                      onClick={() => i18n.changeLanguage('mr')}
                      className={cn(
                        "px-4 py-1.5 rounded-full text-[11px] font-bold tracking-wider transition-all duration-300",
                        i18n.language === 'mr'
                          ? "bg-primary-dark text-white shadow-sm"
                          : "text-gray-500 hover:text-primary-dark hover:bg-gray-200/50"
                      )}
                    >
                      मराठी
                    </button>
                    <button
                      onClick={() => i18n.changeLanguage('en')}
                      className={cn(
                        "px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300",
                        i18n.language === 'en'
                          ? "bg-primary-dark text-white shadow-sm"
                          : "text-gray-500 hover:text-primary-dark hover:bg-gray-200/50"
                      )}
                    >
                      EN
                    </button>
                  </div>
                </div>

                {navLinks.map((link) => (
                  <div key={link.name} className="mb-6">
                    {link.dropdown ? (
                      <div>
                        <button 
                          className="flex justify-between items-center w-full text-lg font-semibold text-primary-dark hover:text-secondary transition-all"
                          onClick={() => setActiveMobileDropdown(activeMobileDropdown === link.name ? null : link.name)}
                        >
                          <span>{t(getNavLinkTranslationKey(link.name))}</span>
                          <ChevronDown className={cn("w-5 h-5 transition-transform duration-300", activeMobileDropdown === link.name && "rotate-180 text-secondary")} />
                        </button>
                        <AnimatePresence>
                          {activeMobileDropdown === link.name && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden bg-gray-50 rounded-2xl mt-2 border border-gray-100/50"
                            >
                              {link.dropdown.map((sub) => {
                                if (sub.subItems) {
                                  const isSubOpen = activeMobileSubDropdown === sub.name;
                                  return (
                                    <div key={sub.name} className="border-b border-gray-100 last:border-0">
                                      <div className="flex justify-between items-center w-full px-6 py-3.5">
                                        <Link
                                          to={sub.path}
                                          className={cn(
                                            "font-semibold text-sm transition-all duration-300 flex-grow",
                                            location.pathname === sub.path ? "text-secondary" : "text-gray-600 hover:text-secondary"
                                          )}
                                        >
                                          {t(getNavLinkTranslationKey(sub.name))}
                                        </Link>
                                        <button
                                          onClick={() => setActiveMobileSubDropdown(isSubOpen ? null : sub.name)}
                                          className="p-1 hover:bg-gray-200/50 rounded transition-all"
                                        >
                                          <ChevronDown className={cn("w-4 h-4 transition-transform duration-300", isSubOpen && "rotate-180 text-secondary")} />
                                        </button>
                                      </div>
                                      <AnimatePresence>
                                        {isSubOpen && (
                                          <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden bg-gray-100/50 pl-4"
                                          >
                                            {sub.subItems.map((child) => (
                                              <Link
                                                key={child.name}
                                                to={child.path}
                                                className={cn(
                                                  "block px-6 py-3.5 font-semibold text-sm transition-all duration-300 border-b border-gray-100 last:border-0",
                                                  location.pathname === child.path 
                                                    ? "text-secondary bg-primary-dark/5 pl-8 border-l-4 border-secondary" 
                                                    : "text-gray-600 hover:text-secondary hover:bg-gray-100 hover:pl-8"
                                                )}
                                              >
                                                {t(getNavLinkTranslationKey(child.name))}
                                              </Link>
                                            ))}
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  );
                                }
                                return (
                                  <Link
                                    key={sub.name}
                                    to={sub.path}
                                    className={cn(
                                      "block px-6 py-3.5 font-semibold text-sm transition-all duration-300 border-b border-gray-100 last:border-0",
                                      location.pathname === sub.path 
                                        ? "text-secondary bg-primary-dark/5 pl-8 border-l-4 border-secondary" 
                                        : "text-gray-600 hover:text-secondary hover:bg-gray-100 hover:pl-8"
                                    )}
                                  >
                                    {t(getNavLinkTranslationKey(sub.name))}
                                  </Link>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={link.path}
                        className={cn(
                          "block text-lg font-semibold transition-all duration-300",
                          location.pathname === link.path 
                            ? "text-secondary pl-2 border-l-4 border-secondary" 
                            : "text-primary-dark hover:text-secondary hover:pl-2"
                        )}
                      >
                        {t(getNavLinkTranslationKey(link.name))}
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              {/* Action and footer CTA block */}
              <div className="p-6 border-t border-gray-100 flex-shrink-0 bg-gray-50">
                <Link
                  to="/apply-loan"
                  className="block w-full bg-primary hover:bg-primary-dark text-white text-center py-4 rounded-xl font-bold shadow-lg shadow-primary/20 transition-all duration-300 transform active:scale-95 live-button-glow"
                >
                  {t('navbar.applyLoanNow')}
                </Link>
                <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-gray-500 font-medium">
                  <Phone className="w-4 h-4 text-secondary" />
                  <a href="tel:+919075251097" className="hover:text-primary transition-colors">
                    Contact: +91 907 525 1097
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
